import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ChevronDown, ChevronRight, AlertTriangle } from "lucide-react";
import { StatusBadge } from "@/components/StatusBadge";
import { useAuth, formatApiErrorDetail } from "@/context/AuthContext";
import { API, fmt } from "@/lib/engagements";

const PHASE_LABEL = { account: "Account", audit: "Audit", tax: "Tax" };

export default function Workload() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [rows, setRows] = useState([]);
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const w = await axios.get(`${API}/workload`);
      setRows(w.data);
      if (isAdmin) {
        const s = await axios.get(`${API}/auth/staff`);
        setStaff(s.data.filter((u) => u.role !== "supervisor"));
      }
    } finally {
      setLoading(false);
    }
  }, [isAdmin]);

  useEffect(() => {
    load();
  }, [load]);

  const reassign = async (job, pic) => {
    try {
      await axios.patch(`${API}/engagements/${job.id}/reassign`, { phase: job.phase, pic: pic || null });
      toast.success(`${job.client} ${PHASE_LABEL[job.phase]} reassigned to ${pic || "Unassigned"}`);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const max = Math.max(1, ...rows.map((r) => r.open_total));
  const names = Array.from(new Set([...staff.map((s) => s.name), ...rows.map((r) => r.pic)]))
    .filter((n) => n && n !== "Unassigned")
    .sort();

  return (
    <div className="space-y-6">
      <div>
        <h2 className="font-display text-3xl font-extrabold">Staff workload</h2>
        <p className="mt-1 text-sm text-zinc-500">
          Open jobs per person in charge, counting only applicable services. Clients already
          finalised or completed are excluded.
          {isAdmin ? " Expand a person to reassign any job." : ""}
        </p>
      </div>

      {loading ? (
        <div className="border border-border">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="h-12 animate-pulse border-b border-border bg-zinc-50" />
          ))}
        </div>
      ) : (
        <div data-testid="workload-table" className="border border-border bg-white">
          <div className="grid grid-cols-[minmax(150px,1fr)_repeat(6,88px)_110px] items-center bg-zinc-900 px-4 py-2 text-[10px] uppercase tracking-widest text-white">
            <span>PIC</span>
            <span className="text-right">Open</span>
            <span className="text-right">Account</span>
            <span className="text-right">Audit</span>
            <span className="text-right">Tax</span>
            <span className="text-right">Overdue</span>
            <span className="text-right">Hrs / wk</span>
            <span className="text-right">Load</span>
          </div>
          {rows.length === 0 && (
            <div data-testid="workload-empty" className="p-10 text-center text-sm text-zinc-500">
              No open jobs assigned.
            </div>
          )}
          {rows.map((r) => {
            const isOpen = open === r.pic;
            return (
              <div key={r.pic} className="border-t border-border">
                <button
                  data-testid={`workload-row-${r.pic.replace(/\s+/g, "-").toLowerCase()}`}
                  onClick={() => setOpen(isOpen ? null : r.pic)}
                  className="grid w-full grid-cols-[minmax(150px,1fr)_repeat(6,88px)_110px] items-center px-4 py-3 text-left text-sm transition-colors hover:bg-zinc-50"
                >
                  <span className="flex items-center gap-2 font-semibold">
                    {isOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                    {r.pic}
                  </span>
                  <span className="text-right font-display text-lg font-extrabold tabular">{r.open_total}</span>
                  <span className="text-right tabular text-zinc-600">{r.account}</span>
                  <span className="text-right tabular text-zinc-600">{r.audit}</span>
                  <span className="text-right tabular text-zinc-600">{r.tax}</span>
                  <span className="text-right tabular">
                    {r.overdue > 0 ? (
                      <span className="inline-flex items-center gap-1 text-red-700">
                        <AlertTriangle className="h-3.5 w-3.5" />
                        {r.overdue}
                      </span>
                    ) : (
                      <span className="text-zinc-400">0</span>
                    )}
                  </span>
                  <span
                    data-testid={`workload-hours-${r.pic.replace(/\s+/g, "-").toLowerCase()}`}
                    className="text-right tabular text-zinc-700"
                  >
                    {Number(r.hours_this_week || 0).toFixed(2)}
                  </span>
                  <span className="flex justify-end">
                    <span className="h-2 w-24 bg-zinc-100">
                      <span
                        className="block h-2 bg-zinc-900"
                        style={{ width: `${Math.round((r.open_total / max) * 100)}%` }}
                      />
                    </span>
                  </span>
                </button>

                {isOpen && (
                  <div className="overflow-x-auto border-t border-border bg-zinc-50 px-4 py-3">
                    <table className="w-full text-left text-xs">
                      <thead className="text-[10px] uppercase tracking-widest text-zinc-500">
                        <tr>
                          <th className="py-1 pr-4">Client</th>
                          <th className="py-1 pr-4">Year End</th>
                          <th className="py-1 pr-4">Task</th>
                          <th className="py-1 pr-4">Status</th>
                          <th className="py-1 pr-4">Last activity</th>
                          <th className="py-1 pr-4">Idle days</th>
                          {isAdmin && <th className="py-1">Reassign to</th>}
                        </tr>
                      </thead>
                      <tbody>
                        {r.jobs.map((j) => (
                          <tr key={`${j.id}-${j.phase}`} className="border-t border-zinc-200">
                            <td className="py-1.5 pr-4 font-semibold">{j.client}</td>
                            <td className="py-1.5 pr-4 tabular">{fmt(j.year_end)}</td>
                            <td className="py-1.5 pr-4">{PHASE_LABEL[j.phase]}</td>
                            <td className="py-1.5 pr-4">
                              <StatusBadge status={j.status} />
                            </td>
                            <td className="py-1.5 pr-4 tabular">{fmt(j.last_activity) || "—"}</td>
                            <td className={`py-1.5 pr-4 tabular ${j.overdue ? "font-semibold text-red-700" : ""}`}>
                              {j.idle_days ?? "—"}
                            </td>
                            {isAdmin && (
                              <td className="py-1.5">
                                <select
                                  data-testid={`reassign-${j.id}-${j.phase}`}
                                  value={r.pic === "Unassigned" ? "" : r.pic}
                                  onChange={(e) => reassign(j, e.target.value)}
                                  className="h-8 rounded-sm border border-input bg-white px-2 text-xs"
                                >
                                  <option value="">Unassigned</option>
                                  {names.map((n) => (
                                    <option key={n} value={n}>
                                      {n}
                                    </option>
                                  ))}
                                </select>
                              </td>
                            )}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
